package er.prateek.crossly.ui.components

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.levelselect_resume
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith
import org.jetbrains.compose.resources.stringResource

/**
 * A level on the map. Three visual states carry the progression rules directly:
 * locked (flat, greyed, padlock), playable (tier-colored face), and *next up*
 * — the first unplayed level, which breathes so the player always knows where
 * to go without reading anything.
 */
@Composable
fun LevelTile(
    number: Int,
    title: String,
    stars: Int,
    locked: Boolean,
    tierColor: Color,
    modifier: Modifier = Modifier,
    isNext: Boolean = false,
    resumePercent: Int? = null,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game
    val interactionSource = remember { MutableInteractionSource() }
    val hovered by interactionSource.collectIsHoveredAsState()

    val inProgress = resumePercent != null && !locked
    val highlight = isNext || inProgress

    val transition = rememberInfiniteTransition(label = "levelTile")
    val pulse = if (highlight) {
        transition.animateFloat(
            initialValue = 1f,
            targetValue = 1.05f,
            animationSpec = infiniteRepeatable(tween(1200), RepeatMode.Reverse),
            label = "nextPulse",
        ).value
    } else {
        1f
    }

    val depth = if (locked) 2.dp else 5.dp
    val face = when {
        locked -> Brush.verticalGradient(listOf(colors.card, colors.card))
        else -> Brush.verticalGradient(
            listOf(tierColor.mixWith(Color.White, 0.12f), tierColor.mixWith(colors.accentAlt, 0.3f)),
        )
    }
    val slab = if (locked) colors.shadow else tierColor.mixWith(Color.Black, 0.4f)
    val shape = RoundedCornerShape(20.dp)

    Column(
        modifier = modifier.graphicsLayer {
            scaleX = pulse
            scaleY = pulse
        },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        Box(modifier = Modifier.fillMaxWidth().padding(bottom = depth)) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .offset(y = depth)
                    .clip(shape)
                    .background(slab),
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(1f)
                    .offset(y = if (hovered && !locked) (-2).dp else 0.dp)
                    .clip(shape)
                    .background(face)
                    .border(
                        width = if (highlight) 2.5.dp else 1.5.dp,
                        color = when {
                            locked -> colors.cardBorder
                            highlight -> colors.gold
                            else -> Color.White.copy(alpha = 0.35f)
                        },
                        shape = shape,
                    )
                    .hoverable(interactionSource, enabled = !locked)
                    .clickable(
                        interactionSource = interactionSource,
                        indication = null,
                        enabled = !locked,
                        onClick = onClick,
                    ),
                contentAlignment = Alignment.Center,
            ) {
                if (locked) {
                    IconLock(size = 26.dp, tint = colors.locked)
                } else if (inProgress) {
                    Column(
                        modifier = Modifier.padding(horizontal = Spacing.xs),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        Text(
                            text = number.toString(),
                            style = MaterialTheme.typography.titleLarge,
                            color = Color.White,
                            textAlign = TextAlign.Center,
                        )
                        Text(
                            text = stringResource(Res.string.levelselect_resume),
                            style = MaterialTheme.typography.labelSmall,
                            color = colors.gold,
                            textAlign = TextAlign.Center,
                        )
                        Text(
                            text = "${resumePercent}%",
                            style = MaterialTheme.typography.labelLarge,
                            color = Color.White.copy(alpha = 0.95f),
                            textAlign = TextAlign.Center,
                        )
                    }
                } else {
                    Text(
                        text = number.toString(),
                        style = MaterialTheme.typography.displaySmall,
                        color = Color.White,
                        textAlign = TextAlign.Center,
                    )
                }
            }
        }

        // Locked tiles still render an empty row so the tile's total height is
        // identical either way and the grid never jumps as levels unlock.
        Box(modifier = Modifier.padding(vertical = 3.dp)) {
            StarRating(stars = if (locked) 0 else stars, starSize = 13.dp)
        }

        Text(
            text = title,
            style = MaterialTheme.typography.labelSmall,
            color = if (locked) colors.locked else colors.inkMuted,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}
